// Shared components for teacher grading: Note and Mark columns
// Used in ResultsPage and PdfAnnotationViewer

import React from 'react';
import {
  TextField,
  Typography,
  Box,
  Stack,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import revisionService from '../../services/RevisionService';
import api from '../../services/api';
import { ANSWERS, QUESTIONS, SUB_ANSWERS } from '../../config/Config';
import MathRenderer from '../common/MathRenderer';

/** API may still return legacy 50/100 on notation/steps from stale rows. */
function normalizeBinaryRubric(v) {
  if (v === 'correct' || v === 'incorrect') return v;
  if (v === '50') return 'incorrect';
  if (v === '100') return 'correct';
  return '';
}

const ACCURACY_MENU = (emptyLabel) => [
  { value: '', label: emptyLabel },
  { value: '0', label: '0%' },
  { value: '50', label: '50%' },
  { value: '100', label: '100%' },
];

// ------------------------------------------------------------------
// Teacher note — always visible inline field; debounced save
// questionId = parent Question FK; subQuestionId optional for sub-parts
// ------------------------------------------------------------------
export const TeacherNoteEditor = ({ attemptId, questionId, subQuestionId = null, revisionData }) => {
  const { user } = useAuth() || {};
  const isTeacher = user?.isTeacher || user?.is_teacher || user?.is_staff;

  const [comment, setComment] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [studentId, setStudentId] = React.useState(null);
  const [noteId, setNoteId] = React.useState(null);
  const debounceRef = React.useRef(null);

  const matchesNote = React.useCallback(
    (n) => {
      if (n.question !== questionId && Number(n.question) !== Number(questionId)) return false;
      if (subQuestionId != null) {
        return Number(n.sub_question) === Number(subQuestionId);
      }
      return n.sub_question == null || n.sub_question === undefined;
    },
    [questionId, subQuestionId]
  );

  React.useEffect(() => {
    if (revisionData && Array.isArray(revisionData)) {
      const note = revisionData.find(matchesNote);
      if (note) {
        setNoteId(note.id);
        setComment(note.teacher_comment || '');
        setStudentId(note.student || null);
      } else {
        setNoteId(null);
        setComment('');
        setStudentId(null);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [revisionData, matchesNote]);

  React.useEffect(() => () => clearTimeout(debounceRef.current), []);

  const persist = React.useCallback(
    async (text) => {
      if (!isTeacher || !attemptId || !questionId) return;
      try {
        setSaving(true);
        const payload = {
          test_attempt: attemptId,
          question: questionId,
          teacher_comment: text,
        };
        if (subQuestionId != null) payload.sub_question = subQuestionId;
        if (studentId) payload.student = studentId;
        if (noteId) {
          await revisionService.update(noteId, payload);
        } else {
          const { data } = await revisionService.create(payload);
          if (data?.id) setNoteId(data.id);
        }
      } catch (err) {
        console.error('Could not save teacher comment', err);
      } finally {
        setSaving(false);
      }
    },
    [attemptId, isTeacher, noteId, questionId, studentId, subQuestionId]
  );

  const onChangeComment = (e) => {
    const v = e.target.value;
    setComment(v);
    if (!isTeacher) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(v.slice(0, 200)), 800);
  };

  if (loading) return <CircularProgress size={18} />;
  if (!isTeacher) {
    return comment ? <Typography variant="body2">{comment}</Typography> : <Typography variant="body2">—</Typography>;
  }

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      size="small"
      value={comment}
      onChange={onChangeComment}
      inputProps={{ maxLength: 200 }}
      helperText={saving ? 'Saving…' : `${comment.length}/200`}
      placeholder="Feedback for the student…"
      disabled={saving}
    />
  );
};

// ------------------------------------------------------------------
// Rubric row: Accuracy (mark_percentage), Notation, Steps — always editable for teachers
// answerQuestionId = parent Question id for main StudentAnswer lookup
// subAnswerId = SubStudentAnswer pk when grading a sub-part
// ------------------------------------------------------------------
export const RubricTripleEditor = React.memo(
  ({ attemptId, answerQuestionId, questionId, subAnswerId = null, registerDeferredSave, onRubricPersisted }) => {
    const aq = answerQuestionId ?? questionId;
    const { user } = useAuth() || {};
    const isTeacher = user?.isTeacher || user?.is_teacher || user?.is_staff;

    const [accuracy, setAccuracy] = React.useState('');
    const [notation, setNotation] = React.useState('');
    const [steps, setSteps] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const studentAnswerIdRef = React.useRef(null);
    const deferPersist = Boolean(registerDeferredSave);
    const stateRef = React.useRef({ accuracy: '', notation: '', steps: '' });
    stateRef.current = { accuracy, notation, steps };
    const dirtyRef = React.useRef(false);

    React.useEffect(() => {
      studentAnswerIdRef.current = null;
      let cancelled = false;
      const load = async () => {
        if (!isTeacher) {
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          if (subAnswerId) {
            const { data } = await api.get(`${SUB_ANSWERS}${subAnswerId}/`);
            if (cancelled || !data) return;
            setAccuracy(data.mark_percentage != null && data.mark_percentage !== '' ? String(data.mark_percentage) : '');
            setNotation(normalizeBinaryRubric(data.rubric_notation));
            setSteps(normalizeBinaryRubric(data.rubric_steps));
            dirtyRef.current = false;
          } else {
            const response = await api.get(ANSWERS, {
              params: { test_attempt: attemptId, question: aq },
            });
            const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
            const row = rows.find((a) => {
              const qid = a.question ?? a.question_id;
              return Number(qid) === Number(aq);
            }) || rows[0];
            if (cancelled) return;
            if (row) {
              studentAnswerIdRef.current = row.id;
              setAccuracy(row.mark_percentage != null && row.mark_percentage !== '' ? String(row.mark_percentage) : '');
              setNotation(normalizeBinaryRubric(row.rubric_notation));
              setSteps(normalizeBinaryRubric(row.rubric_steps));
            } else {
              setAccuracy('');
              setNotation('');
              setSteps('');
            }
            dirtyRef.current = false;
          }
        } catch (err) {
          console.error('Rubric load failed', err);
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [attemptId, aq, subAnswerId, isTeacher]);

    const persist = React.useCallback(
      async (nextAcc, nextNot, nextSteps) => {
        if (!isTeacher) return;
        setSaving(true);
        const body = {
          mark_percentage: nextAcc || null,
          rubric_notation: nextNot || null,
          rubric_steps: nextSteps || null,
        };
        try {
          if (subAnswerId) {
            await api.patch(`${SUB_ANSWERS}${subAnswerId}/`, body);
          } else {
            let sid = studentAnswerIdRef.current;
            if (!sid) {
              const createRes = await api.post(ANSWERS, {
                test_attempt: attemptId,
                question: aq,
                answer: '',
                ...body,
              });
              sid = createRes.data?.id;
              studentAnswerIdRef.current = sid;
            } else {
              await api.patch(`${ANSWERS}${sid}/`, body);
            }
          }
          onRubricPersisted?.({
            questionId: aq,
            subAnswerId: subAnswerId ?? null,
            mark_percentage: body.mark_percentage,
            rubric_notation: body.rubric_notation,
            rubric_steps: body.rubric_steps,
          });
        } catch (err) {
          console.error('Rubric save failed', err);
        } finally {
          setSaving(false);
        }
      },
      [aq, attemptId, isTeacher, subAnswerId, onRubricPersisted]
    );

    React.useEffect(() => {
      if (!registerDeferredSave || !isTeacher) return undefined;
      const flush = async () => {
        if (!dirtyRef.current) return;
        const { accuracy: acc, notation: not, steps: st } = stateRef.current;
        await persist(acc, not, st);
        dirtyRef.current = false;
      };
      return registerDeferredSave(flush);
    }, [registerDeferredSave, isTeacher, persist]);

    const onPick = (field, value) => {
      let na = accuracy;
      let nn = notation;
      let ns = steps;
      if (field === 'accuracy') {
        na = value;
        setAccuracy(value);
      } else if (field === 'notation') {
        nn = value;
        setNotation(value);
      } else {
        ns = value;
        setSteps(value);
      }
      if (deferPersist) dirtyRef.current = true;
      else persist(na, nn, ns);
    };

    if (loading) return <CircularProgress size={18} />;
    if (!isTeacher) {
      const binLabel = (v) => (v === 'correct' ? 'Correct' : v === 'incorrect' ? 'Incorrect' : null);
      const nNorm = normalizeBinaryRubric(notation);
      const sNorm = normalizeBinaryRubric(steps);
      const parts = [
        accuracy !== '' && accuracy != null && `Accuracy ${accuracy}%`,
        binLabel(nNorm) && `Notation: ${binLabel(nNorm)}`,
        binLabel(sNorm) && `Steps: ${binLabel(sNorm)}`,
      ].filter(Boolean);
      return <Typography variant="body2">{parts.length ? parts.join(' · ') : '—'}</Typography>;
    }

    const accuracyCell = (
      <Box sx={{ width: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em', display: 'block', mb: 0.35 }}>
          Accuracy
        </Typography>
        <FormControl size="small" fullWidth>
          <Select
            value={accuracy}
            onChange={(e) => onPick('accuracy', e.target.value)}
            displayEmpty
            disabled={saving}
          >
            {ACCURACY_MENU('—').map((o) => (
              <MenuItem key={o.value || 'empty'} value={o.value}>
                {o.value !== '' ? o.label : <em>{o.label}</em>}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );

    const binaryTablets = (label, field, value) => (
      <Box sx={{ width: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em', display: 'block', mb: 0.35 }}>
          {label}
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={value === '' || value == null ? null : value}
          onChange={(_, v) => onPick(field, v == null ? '' : v)}
          disabled={saving}
          fullWidth
          sx={{
            display: 'flex',
            '& .MuiToggleButton-root': {
              flex: 1,
              py: 1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: '10px !important',
              border: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                bgcolor: 'action.selected',
              },
            },
            '& .MuiToggleButton-root[value="correct"].Mui-selected': {
              bgcolor: 'success.main',
              color: 'success.contrastText',
              borderColor: 'success.dark',
            },
            '& .MuiToggleButton-root[value="incorrect"].Mui-selected': {
              bgcolor: 'error.main',
              color: 'error.contrastText',
              borderColor: 'error.dark',
            },
          }}
        >
          <ToggleButton value="correct" aria-label={`${label} correct`}>
            Correct
          </ToggleButton>
          <ToggleButton value="incorrect" aria-label={`${label} incorrect`}>
            Incorrect
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );

    return (
      <Stack spacing={2} sx={{ width: '100%' }}>
        {accuracyCell}
        {binaryTablets('Notation', 'notation', notation)}
        {binaryTablets('Steps', 'steps', steps)}
      </Stack>
    );
  },
  (prev, next) =>
    prev.attemptId === next.attemptId &&
    (prev.answerQuestionId ?? prev.questionId) === (next.answerQuestionId ?? next.questionId) &&
    prev.subAnswerId === next.subAnswerId &&
    prev.registerDeferredSave === next.registerDeferredSave
);

/** Alias: same as RubricTripleEditor (supports legacy `questionId` prop). */
export const MarkEditor = RubricTripleEditor;

// ------------------------------------------------------------------
// Correct Answer editor (editable if teacher owns test, view-only otherwise)
// Shows dropdown for multiple choice, text field for short answers
// ------------------------------------------------------------------
export const CorrectAnswerEditor = React.memo(({ questionId, correctAnswer, isEditable, question, placeholder = 'Enter correct answer', compact = false }) => {
  const [saving, setSaving] = React.useState(false);
  const saveTimeoutRef = React.useRef(null);

  // Determine if question has options (multiple choice)
  const hasOptions = question?.options && Array.isArray(question.options) && question.options.length > 0;
  const questionType = (question?.question_type || question?.type || '').toUpperCase();
  const isTrueFalse = questionType === 'TRUE_FALSE';

  // Normalize correct answer value for Select dropdown (match option ID/value)
  const normalizedValue = React.useMemo(() => {
    if (isTrueFalse) {
      const v = String(correctAnswer || '').trim();
      return v.toLowerCase() === 'false' ? 'False' : (v.toLowerCase() === 'true' ? 'True' : (correctAnswer || ''));
    }
    if (!hasOptions || !correctAnswer) return correctAnswer || '';
    
    // Try to find matching option
    const matchingOption = question.options.find((opt, idx) => {
      if (typeof opt === 'object' && opt !== null) {
        const optId = String(opt.id || opt.option_id || opt.value || (idx + 1));
        const optText = String(opt.text || opt.option_text || opt.answer_text || opt.label || '');
        return optId === String(correctAnswer) || optText === String(correctAnswer);
      }
      return String(opt) === String(correctAnswer) || String(idx + 1) === String(correctAnswer);
    });
    
    if (matchingOption) {
      if (typeof matchingOption === 'object' && matchingOption !== null) {
        return String(matchingOption.id || matchingOption.option_id || matchingOption.value || 
          question.options.indexOf(matchingOption) + 1);
      }
      return String(question.options.indexOf(matchingOption) + 1);
    }
    
    return correctAnswer || '';
  }, [correctAnswer, hasOptions, question, isTrueFalse]);

  const [value, setValue] = React.useState(() => {
    // Initialize with correctAnswer, will be normalized in useEffect
    return correctAnswer || '';
  });

  React.useEffect(() => {
    setValue(normalizedValue);
  }, [normalizedValue]);

  // Auto-save with debounce
  const handleChange = React.useCallback(async (newValue) => {
    setValue(newValue);
    
    if (!isEditable || newValue === normalizedValue) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 1 second
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        // For multiple choice, save the option text or ID depending on backend expectation
        // Try to get option text if it's an option ID
        let valueToSave = newValue;
        if (isTrueFalse && newValue) {
          valueToSave = newValue === 'False' ? 'False' : 'True';
        } else if (hasOptions && newValue) {
          const selectedOption = question.options.find((opt, idx) => {
            if (typeof opt === 'object' && opt !== null) {
              const optId = String(opt.id || opt.option_id || opt.value || (idx + 1));
              return optId === String(newValue);
            }
            return String(idx + 1) === String(newValue);
          });
          
          if (selectedOption) {
            // Save option text for better readability (backend can handle both)
            if (typeof selectedOption === 'object' && selectedOption !== null) {
              valueToSave = selectedOption.text || selectedOption.option_text || selectedOption.answer_text || selectedOption.label || newValue;
            } else {
              valueToSave = String(selectedOption);
            }
          }
        }
        
        await api.patch(`${QUESTIONS}${questionId}/`, { correct_answer: valueToSave });
      } catch (err) {
        console.error(`Error updating correct answer for question ${questionId}:`, err);
        setValue(normalizedValue); // Revert on error
      } finally {
        setSaving(false);
      }
    }, 1000);
  }, [questionId, normalizedValue, isEditable, hasOptions, question, isTrueFalse]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!isEditable) {
    // View-only mode: display the answer (use MathRenderer for formulas)
    if (isTrueFalse) {
      const tfText = value === 'False' ? 'False' : (value === 'True' ? 'True' : (correctAnswer || '—'));
      return <MathRenderer content={tfText} variant="body2" />;
    }
    if (hasOptions && value) {
      const option = question.options.find(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return String(opt.id || opt.option_id || opt.value || '') === String(value) ||
                 String(opt.text || opt.option_text || opt.answer_text || opt.label || '') === String(value);
        }
        return String(opt) === String(value);
      });
      const displayText = option 
        ? (typeof option === 'object' ? (option.text || option.option_text || option.answer_text || option.label || value) : option)
        : value;
      return <MathRenderer content={String(displayText || '—')} variant="body2" />;
    }
    return <MathRenderer content={String(correctAnswer || '—')} variant="body2" />;
  }

  // Editable mode: True/False — show dropdown with True and False
  if (isTrueFalse) {
    return (
      <FormControl size="small" sx={{ minWidth: compact ? 0 : 120, width: compact ? '100%' : undefined, maxWidth: '100%' }}>
        <Select
          value={value === 'True' || value === 'False' ? value : ''}
          onChange={(e) => handleChange(e.target.value)}
          displayEmpty
          disabled={saving}
        >
          <MenuItem value=""><em>Select answer</em></MenuItem>
          <MenuItem value="True">True</MenuItem>
          <MenuItem value="False">False</MenuItem>
        </Select>
        {saving && <CircularProgress size={16} sx={{ ml: 1, position: 'absolute', right: 8 }} />}
      </FormControl>
    );
  }

  // Editable mode: Multiple choice — show dropdown with options
  if (hasOptions && (questionType === 'MULTIPLE' || questionType === 'MULTIPLE_CHOICE')) {
    return (
      <FormControl size="small" sx={{ minWidth: compact ? 0 : 150, width: compact ? '100%' : undefined, maxWidth: '100%' }}>
        <Select
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          displayEmpty
          disabled={saving}
        >
          <MenuItem value=""><em>Select answer</em></MenuItem>
          {question.options.map((opt, idx) => {
            const optionId = typeof opt === 'object' && opt !== null 
              ? (opt.id || opt.option_id || opt.value || (idx + 1))
              : (idx + 1);
            const optionText = typeof opt === 'object' && opt !== null
              ? (opt.text || opt.option_text || opt.answer_text || opt.label || `Option ${optionId}`)
              : String(opt);
            const optionValue = typeof opt === 'object' && opt !== null
              ? String(opt.id || opt.option_id || opt.value || optionId)
              : String(optionId);
            
            return (
              <MenuItem key={optionId} value={optionValue}>
                {optionText}
              </MenuItem>
            );
          })}
        </Select>
        {saving && <CircularProgress size={16} sx={{ ml: 1, position: 'absolute', right: 8 }} />}
      </FormControl>
    );
  }

  // Text answer: show text field (placeholder acts as inline label when no separate label)
  return (
    <TextField
      size="small"
      value={value || ''}
      onChange={(e) => handleChange(e.target.value)}
      disabled={saving}
      multiline
      maxRows={3}
      sx={{ minWidth: compact ? 0 : 150, width: compact ? '100%' : undefined, maxWidth: '100%', boxSizing: 'border-box' }}
      placeholder={placeholder}
    />
  );
}, (prev, next) => 
  prev.questionId === next.questionId && 
  prev.correctAnswer === next.correctAnswer && 
  prev.isEditable === next.isEditable &&
  JSON.stringify(prev.question?.options) === JSON.stringify(next.question?.options) &&
  prev.question?.question_type === next.question?.question_type &&
  prev.placeholder === next.placeholder &&
  prev.compact === next.compact
);
